import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FiClock, FiCalendar, FiCheckCircle, FiXCircle, FiAlertCircle, FiUser, FiChevronDown, FiChevronUp, FiCoffee, FiPause, FiHeart, FiMoreHorizontal, FiCheckSquare, FiEdit, FiEyeOff, FiSend, FiEdit2, FiPlusCircle, FiTrendingUp } from 'react-icons/fi'
import SolicitudesHorasExtras from './SolicitudesHorasExtras'
import toast from 'react-hot-toast'
import { canAccess, getEffectivePlan } from '../config/planFeatures'
import { useAuth } from '../contexts/AuthContext'
import { SkeletonTable, SkeletonStats } from '../components/SkeletonLoader'
import PropuestaCorreccionModal from '../components/PropuestaCorreccionModal'
import PropuestaMarcajeModal from '../components/PropuestaMarcajeModal'
import { API_URL } from '../config/api'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { PageHeader } from '../ui'
import { formatearFecha } from '../utils/fechas'

const TIPOS_PAUSA = {
  comida: { nombre: 'Comida', icono: FiCoffee, color: 'bg-orange-100 text-orange-600' },
  descanso: { nombre: 'Descanso', icono: FiPause, color: 'bg-blue-100 text-blue-600' },
  medico: { nombre: 'Médico', icono: FiHeart, color: 'bg-red-100 text-red-600' },
  personal: { nombre: 'Personal', icono: FiUser, color: 'bg-purple-100 text-purple-600' },
  otro: { nombre: 'Otro', icono: FiMoreHorizontal, color: 'bg-[var(--bg-2)] text-[var(--ink-2)]' }
}

const GestionFichajes = () => {
  const { empresaInfo } = useAuth()
  const plan = getEffectivePlan(empresaInfo)
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'fichajes')
  const [loading, setLoading] = useState(true)

  // Fichajes
  const [fichajes, setFichajes] = useState([])
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState('')
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1)
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear())
  const [fichajeExpandido, setFichajeExpandido] = useState(null)

  // Correcciones
  const [correcciones, setCorrecciones] = useState([])
  const [contadorCorrecciones, setContadorCorrecciones] = useState(0)

  // Ausencias (futuras = permisos, pasadas = justificaciones que van a correcciones)
  const [ausencias, setAusencias] = useState([])
  const [ausenciasPasadas, setAusenciasPasadas] = useState([])
  const [contadorAusencias, setContadorAusencias] = useState(0)

  // Control (días sin fichaje)
  const [diasSinFichaje, setDiasSinFichaje] = useState([])
  const [contadorControl, setContadorControl] = useState(0)
  const [estadoFiltroControl, setEstadoFiltroControl] = useState('pendiente')

  // Sub-pestaña dentro de correcciones
  const [subTabCorrecciones, setSubTabCorrecciones] = useState('solicitudes')

  // Filtro estado de solicitudes (correcciones, ausencias, horas extras):
  // 'pendiente' (default, solo pendientes) | 'historico' (aprobadas + rechazadas)
  const [estadoFiltroSolicitudes, setEstadoFiltroSolicitudes] = useState('pendiente')

  // Multi-select días sin fichaje
  const [diasSeleccionados, setDiasSeleccionados] = useState([])
  const [modalPropuestaMarcaje, setModalPropuestaMarcaje] = useState(null)

  // Empleados
  const [empleados, setEmpleados] = useState([])

  // Modal de comentario
  const [modalComentario, setModalComentario] = useState({
    visible: false,
    tipo: '', // 'correccion' o 'ausencia'
    accion: '', // 'aprobar' o 'rechazar'
    id: null,
    titulo: '',
    placeholder: '',
    requerido: false
  })
  const [comentarioTexto, setComentarioTexto] = useState('')

  // Modal propuesta de corrección (Avanzado)
  const [fichajeParaPropuesta, setFichajeParaPropuesta] = useState(null)

  // Modales de edición/inserción directa (Pro)
  const [modalAdminEdit, setModalAdminEdit] = useState(null) // fichaje a editar
  const [modalAdminInsert, setModalAdminInsert] = useState(null) // dia sin fichaje a insertar
  const [adminEditForm, setAdminEditForm] = useState({ hora_entrada: '', hora_salida: '', motivo: '' })
  const [adminInsertForm, setAdminInsertForm] = useState({ hora_entrada: '', hora_salida: '', motivo: '' })
  const [savingAdmin, setSavingAdmin] = useState(false)

  // Helper para construir URL con empresa_id si estamos en modo gestión
  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaGestionId) {
      const separator = endpoint.includes('?') ? '&' : '?'
      return `${API_URL}${endpoint}${separator}empresa_id=${empresaGestionId}`
    }
    return `${API_URL}${endpoint}`
  }

  // Helper para abrir modal de comentario
  const abrirModalComentario = (tipo, accion, id) => {
    let titulo = ''
    let placeholder = ''
    let requerido = false

    if (tipo === 'control_ignorar') {
      titulo = 'Ignorar dia sin fichaje'
      placeholder = 'Motivo (festivo, dia no laborable, etc.)'
      requerido = false
    } else if (accion === 'aprobar') {
      titulo = tipo === 'correccion' ? 'Aprobar corrección' : 'Aprobar ausencia'
      placeholder = 'Comentario (opcional)'
      requerido = false
    } else {
      titulo = tipo === 'correccion' ? 'Rechazar corrección' : 'Rechazar ausencia'
      placeholder = 'Motivo del rechazo'
      requerido = true
    }

    setModalComentario({
      visible: true,
      tipo,
      accion,
      id,
      titulo,
      placeholder,
      requerido
    })
    setComentarioTexto('')
  }

  // Handler para enviar el comentario
  const handleEnviarComentario = () => {
    if (modalComentario.requerido && !comentarioTexto.trim()) {
      toast.error('El comentario es requerido')
      return
    }

    const { tipo, accion, id } = modalComentario

    if (tipo === 'correccion') {
      handleResponderCorreccion(id, accion, comentarioTexto)
    } else if (tipo === 'ausencia') {
      handleResponderAusencia(id, accion, comentarioTexto)
    } else if (tipo === 'control_ignorar') {
      handleIgnorarDia(id, comentarioTexto)
    }

    // Cerrar modal
    setModalComentario({ ...modalComentario, visible: false })
    setComentarioTexto('')
  }

  // Reaccionar a cambios en el parámetro ?tab= de la URL (navegación desde notificaciones)
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['fichajes', 'correcciones', 'ausencias'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  useEffect(() => {
    cargarEmpleados()
    cargarContadores()
  }, [empresaGestionId])

  const cargarContadores = async () => {
    try {
      const token = localStorage.getItem('token')
      const hoy = new Date().toISOString().split('T')[0]

      let numCorrecciones = 0
      let numAusenciasPasadas = 0

      const resCorrecciones = await fetch(buildApiUrl('/correcciones/todas?estado=pendiente'), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (resCorrecciones.ok) {
        const dataCorrecciones = await resCorrecciones.json()
        numCorrecciones = dataCorrecciones.length
      }

      const resAusencias = await fetch(buildApiUrl('/ausencias/todas?estado=pendiente'), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (resAusencias.ok) {
        const dataAusencias = await resAusencias.json()
        numAusenciasPasadas = dataAusencias.filter(a => a.fecha_fin < hoy).length
        setContadorAusencias(dataAusencias.filter(a => a.fecha_fin >= hoy).length)
      }

      setContadorCorrecciones(numCorrecciones + numAusenciasPasadas)

      const resControl = await fetch(buildApiUrl('/control-fichajes/contador'), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (resControl.ok) {
        const dataControl = await resControl.json()
        setContadorControl(dataControl.count || 0)
      }
    } catch (error) {
      console.error('Error cargando contadores:', error)
    }
  }

  useEffect(() => {
    if (activeTab === 'fichajes') {
      cargarFichajes()
    } else if (activeTab === 'correcciones') {
      cargarCorrecciones()
      cargarDiasSinFichaje()
      cargarAusencias()
    }
  }, [activeTab, empleadoSeleccionado, mesSeleccionado, anioSeleccionado, estadoFiltroControl, estadoFiltroSolicitudes])

  // Detectar cambio de día y recargar si estamos viendo el mes actual
  useEffect(() => {
    let fechaActual = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })

    const checkDayChange = setInterval(() => {
      const nuevaFecha = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
      if (nuevaFecha !== fechaActual) {
        console.log('🌅 Cambio de día detectado en gestión, verificando si recargar...')
        fechaActual = nuevaFecha

        // Solo recargar si estamos viendo el mes actual
        const ahora = new Date()
        const mesActual = (ahora.getMonth() + 1).toString().padStart(2, '0')
        const anioActual = ahora.getFullYear().toString()

        if (mesSeleccionado === mesActual && anioSeleccionado === anioActual) {
          console.log('📊 Recargando datos del mes actual...')
          cargarContadores()
          if (activeTab === 'fichajes') {
            cargarFichajes()
          } else if (activeTab === 'correcciones') {
            cargarCorrecciones()
            cargarDiasSinFichaje()
          }
        }
      }
    }, 60000) // Verificar cada minuto

    return () => clearInterval(checkDayChange)
  }, [activeTab, mesSeleccionado, anioSeleccionado])

  const cargarEmpleados = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/empleados'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setEmpleados(data.empleados || [])
      }
    } catch (error) {
      console.error('Error al cargar empleados:', error)
    }
  }

  const cargarFichajes = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      let endpoint = `/fichajes/todos?mes=${mesSeleccionado}&anio=${anioSeleccionado}`

      if (empleadoSeleccionado) {
        endpoint += `&empleado_id=${empleadoSeleccionado}`
      }

      const url = buildApiUrl(endpoint)
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setFichajes(data)
      }
    } catch (error) {
      toast.error('Error al cargar fichajes')
    } finally {
      setLoading(false)
    }
  }

  const cargarCorrecciones = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      // Histórico → fetch todas y filtrar client-side a no-pendientes
      const estadoQuery = estadoFiltroSolicitudes === 'historico' ? 'todas' : 'pendiente'
      let endpoint = `/correcciones/todas?estado=${estadoQuery}`

      if (empleadoSeleccionado) {
        endpoint += `&empleado_id=${empleadoSeleccionado}`
      }

      const url = buildApiUrl(endpoint)
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        let data = await response.json()
        if (estadoFiltroSolicitudes === 'historico') {
          data = data.filter(c => c.estado !== 'pendiente')
        }
        setCorrecciones(data)
      }
    } catch (error) {
      toast.error('Error al cargar correcciones')
    } finally {
      setLoading(false)
    }
  }

  const cargarAusencias = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const estadoQuery = estadoFiltroSolicitudes === 'historico' ? 'todas' : 'pendiente'
      let endpoint = `/ausencias/todas?estado=${estadoQuery}`

      if (empleadoSeleccionado) {
        endpoint += `&empleado_id=${empleadoSeleccionado}`
      }

      const url = buildApiUrl(endpoint)
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        let data = await response.json()
        if (estadoFiltroSolicitudes === 'historico') {
          data = data.filter(a => a.estado !== 'pendiente')
        }
        const hoy = new Date().toISOString().split('T')[0]
        // Ausencias con fecha_fin pasada → son justificaciones de fichajes olvidados → van a correcciones
        setAusenciasPasadas(data.filter(a => a.fecha_fin < hoy))
        // Ausencias con fecha_fin futura o de hoy → son permisos/vacaciones → van a solicitud permisos
        setAusencias(data.filter(a => a.fecha_fin >= hoy))
      }
    } catch (error) {
      toast.error('Error al cargar ausencias')
    } finally {
      setLoading(false)
    }
  }

  const cargarDiasSinFichaje = async () => {
    setLoading(true)
    setDiasSeleccionados([])
    try {
      const token = localStorage.getItem('token')
      let endpoint = `/control-fichajes/dias-sin-fichaje?estado=${estadoFiltroControl}`

      if (empleadoSeleccionado) {
        endpoint += `&empleado_id=${empleadoSeleccionado}`
      }

      const url = buildApiUrl(endpoint)
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setDiasSinFichaje(data)
      }
    } catch (error) {
      toast.error('Error al cargar días sin fichaje')
    } finally {
      setLoading(false)
    }
  }

  const handleIgnorarDia = async (id, observaciones = '') => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl(`/control-fichajes/${id}/ignorar`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ observaciones })
      })

      if (response.ok) {
        toast.success('Día marcado como ignorado')
        cargarDiasSinFichaje()
        cargarContadores()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al ignorar día')
      }
    } catch (error) {
      toast.error('Error al ignorar día')
    }
  }

  // Multi-select handlers para días sin fichaje
  const toggleDiaSeleccionado = (diaId) => {
    setDiasSeleccionados(prev =>
      prev.includes(diaId)
        ? prev.filter(id => id !== diaId)
        : [...prev, diaId]
    )
  }

  const toggleSeleccionarTodos = () => {
    const pendientes = diasSinFichaje.filter(d => d.estado === 'pendiente')
    if (diasSeleccionados.length === pendientes.length && diasSeleccionados.length > 0) {
      setDiasSeleccionados([])
    } else {
      setDiasSeleccionados(pendientes.map(d => d.id))
    }
  }

  const abrirPropuestaMarcaje = (dias) => {
    const diasArray = Array.isArray(dias) ? dias : [dias]
    setModalPropuestaMarcaje(diasArray)
  }

  const handlePropuestaMarcajeSuccess = () => {
    setModalPropuestaMarcaje(null)
    setDiasSeleccionados([])
    cargarDiasSinFichaje()
    cargarContadores()
  }

  const handleResponderCorreccion = async (id, accion, comentario = '') => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl(`/correcciones/${id}/responder`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ accion, comentario })
      })

      if (response.ok) {
        toast.success(`Corrección ${accion === 'aprobar' ? 'aprobada' : 'rechazada'}`)
        cargarCorrecciones()
        cargarContadores()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al procesar solicitud')
      }
    } catch (error) {
      toast.error('Error al procesar solicitud')
    }
  }

  const handleResponderAusencia = async (id, accion, comentario = '') => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl(`/ausencias/${id}/responder`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ accion, comentario })
      })

      if (response.ok) {
        toast.success(`Ausencia ${accion === 'aprobar' ? 'aprobada' : 'rechazada'}`)
        cargarAusencias()
        cargarContadores()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al procesar solicitud')
      }
    } catch (error) {
      toast.error('Error al procesar solicitud')
    }
  }

  // Edición directa de fichaje existente (Plan Pro)
  const handleAdminEditFichaje = async () => {
    if (!adminEditForm.hora_entrada || !adminEditForm.hora_salida) {
      toast.error('La hora de entrada y salida son obligatorias')
      return
    }
    if (!adminEditForm.motivo.trim()) {
      toast.error('El motivo es obligatorio')
      return
    }
    setSavingAdmin(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl(`/fichajes/${modalAdminEdit.id}/admin-edit`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(adminEditForm)
      })
      if (response.ok) {
        toast.success('Fichaje corregido correctamente')
        setModalAdminEdit(null)
        cargarFichajes()
      } else {
        const err = await response.json()
        toast.error(err.error || 'Error al corregir fichaje')
      }
    } catch {
      toast.error('Error al corregir fichaje')
    } finally {
      setSavingAdmin(false)
    }
  }

  // Inserción directa de fichaje faltante (Plan Pro)
  const handleAdminInsertFichaje = async () => {
    if (!adminInsertForm.hora_entrada || !adminInsertForm.hora_salida) {
      toast.error('La hora de entrada y salida son obligatorias')
      return
    }
    if (!adminInsertForm.motivo.trim()) {
      toast.error('El motivo es obligatorio')
      return
    }
    setSavingAdmin(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/fichajes/admin-insert'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          empleado_id: modalAdminInsert.empleado_id,
          fecha: modalAdminInsert.fecha,
          hora_entrada: adminInsertForm.hora_entrada,
          hora_salida: adminInsertForm.hora_salida,
          motivo: adminInsertForm.motivo
        })
      })
      if (response.ok) {
        toast.success('Fichaje insertado correctamente')
        setModalAdminInsert(null)
        cargarDiasSinFichaje()
        cargarContadores()
      } else {
        const err = await response.json()
        toast.error(err.error || 'Error al insertar fichaje')
      }
    } catch {
      toast.error('Error al insertar fichaje')
    } finally {
      setSavingAdmin(false)
    }
  }

  const formatearDuracion = (minutos) => {
    if (!minutos && minutos !== 0) return '-'
    const horas = Math.floor(minutos / 60)
    const mins = minutos % 60
    return `${horas}h ${mins}m`
  }

  const toggleExpandir = (id) => {
    setFichajeExpandido(fichajeExpandido === id ? null : id)
  }

  const getTipoPausaInfo = (tipo) => {
    return TIPOS_PAUSA[tipo] || TIPOS_PAUSA.otro
  }

  const getEstadoBadge = (fichaje) => {
    if (fichaje.hora_salida) {
      return <span className="badge badge-success">Completo</span>
    } else if (fichaje.estado === 'pausado') {
      return <span className="badge badge-warning">Pausado</span>
    } else {
      return <span className="badge badge-primary">En curso</span>
    }
  }

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ]

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ GF"
        kicker="Gestión fichajes"
        title="Fichajes y"
        italic="solicitudes."
        sub="Administra fichajes, correcciones, ausencias y horas extras del equipo."
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Pestañas - Segment Control */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-1 mb-6 inline-flex flex-wrap gap-1">
        <button
          onClick={() => setActiveTab('fichajes')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'fichajes'
              ? 'bg-white shadow-md text-blue-600'
              : 'text-[var(--ink-2)] hover:text-[var(--ink)]'
          }`}
        >
          <FiClock className="mr-2 h-4 w-4" />
          Fichajes
        </button>
        <button
          onClick={() => setActiveTab('correcciones')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'correcciones'
              ? 'bg-white shadow-md text-blue-600'
              : 'text-[var(--ink-2)] hover:text-[var(--ink)]'
          }`}
        >
          <FiAlertCircle className="mr-2 h-4 w-4" />
          Correcciones
          {(() => {
            const total = (canAccess(plan, 'correcciones_flujo') ? contadorCorrecciones : 0) + contadorControl + (canAccess(plan, 'ausencias') ? contadorAusencias : 0)
            return total > 0 && (
              <span className="ml-2 badge badge-danger badge-pulse">{total}</span>
            )
          })()}
        </button>
        <button
          onClick={() => setActiveTab('horasExtras')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'horasExtras'
              ? 'bg-white shadow-md text-blue-600'
              : 'text-[var(--ink-2)] hover:text-[var(--ink)]'
          }`}
        >
          <FiTrendingUp className="mr-2 h-4 w-4" />
          Horas extras
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[var(--ink-3)] whitespace-nowrap">Empleado</label>
          <select
            value={empleadoSeleccionado}
            onChange={(e) => setEmpleadoSeleccionado(e.target.value)}
            className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] text-sm"
          >
            <option value="">Todos</option>
            {empleados.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.nombre}
              </option>
            ))}
          </select>
        </div>

        {activeTab === 'fichajes' && (
          <>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-[var(--ink-3)] whitespace-nowrap">Mes</label>
              <select
                value={mesSeleccionado}
                onChange={(e) => setMesSeleccionado(parseInt(e.target.value))}
                className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] text-sm"
              >
                {meses.map((mes, index) => (
                  <option key={index + 1} value={index + 1}>
                    {mes}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-[var(--ink-3)] whitespace-nowrap">Año</label>
              <select
                value={anioSeleccionado}
                onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
                className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] text-sm"
              >
                {anios.map((anio) => (
                  <option key={anio} value={anio}>
                    {anio}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {loading ? (
        <SkeletonTable rows={5} cols={6} />
      ) : (
        <>
          {/* Tabla de Fichajes */}
          {activeTab === 'fichajes' && (
            <div className="ui-card p-5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Empleado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Entrada
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Salida
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Pausas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Tiempo efectivo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fichajes.map((fichaje) => {
                      const tienePausas = fichaje.pausas && fichaje.pausas.length > 0
                      const estaExpandido = fichajeExpandido === fichaje.id

                      return (
                        <>
                          <tr key={fichaje.id} className="hover:bg-[var(--bg-2)]">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <FiUser className="mr-2 h-4 w-4 text-[var(--ink-4)]" />
                                <span className="text-sm text-[var(--ink)]">{fichaje.empleado_nombre}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-sm text-[var(--ink)]">{formatearFecha(fichaje.fecha)}</span>
                                {fichaje.tiene_correccion === 1 && (
                                  <span className="ml-2 badge badge-warning">
                                    Corregido
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--ink)]">
                              {fichaje.hora_entrada}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--ink)]">
                              {fichaje.hora_salida ? (
                                <>
                                  {fichaje.hora_salida}
                                  {fichaje.cross_day && <span className="text-xs text-orange-600 font-semibold ml-1">(+1D)</span>}
                                </>
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {tienePausas ? (
                                <button
                                  onClick={() => toggleExpandir(fichaje.id)}
                                  className="flex items-center text-sm text-primary-600 hover:text-primary-800"
                                >
                                  <span className="badge badge-warning">
                                    {fichaje.pausas.length} pausa{fichaje.pausas.length > 1 ? 's' : ''}
                                  </span>
                                  {fichaje.tiempo_pausas > 0 && (
                                    <span className="ml-2 text-xs text-[var(--ink-3)]">
                                      ({formatearDuracion(fichaje.tiempo_pausas)})
                                    </span>
                                  )}
                                  {estaExpandido ? (
                                    <FiChevronUp className="ml-1 h-4 w-4" />
                                  ) : (
                                    <FiChevronDown className="ml-1 h-4 w-4" />
                                  )}
                                </button>
                              ) : (
                                <span className="text-sm text-[var(--ink-4)]">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--ink)]">
                              {formatearDuracion(fichaje.tiempo_efectivo || fichaje.tiempo_total)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getEstadoBadge(fichaje)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {canAccess(plan, 'correcciones_flujo') ? (
                                <button
                                  onClick={() => setFichajeParaPropuesta(fichaje)}
                                  className="text-primary-600 hover:text-primary-900 flex items-center text-sm"
                                  title="Proponer corrección al empleado"
                                >
                                  <FiSend className="h-4 w-4 mr-1" />
                                  Proponer
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setAdminEditForm({
                                      hora_entrada: fichaje.hora_entrada || '',
                                      hora_salida: fichaje.hora_salida || '',
                                      motivo: ''
                                    })
                                    setModalAdminEdit(fichaje)
                                  }}
                                  className="text-amber-600 hover:text-amber-800 flex items-center text-sm"
                                  title="Corregir fichaje directamente"
                                >
                                  <FiEdit2 className="h-4 w-4 mr-1" />
                                  Corregir
                                </button>
                              )}
                            </td>
                          </tr>
                          {/* Fila expandible con detalle de pausas */}
                          {tienePausas && estaExpandido && (
                            <tr key={`${fichaje.id}-pausas`} className="bg-gradient-to-r from-gray-50 to-gray-100">
                              <td colSpan="8" className="px-6 py-4">
                                <div className="ml-8">
                                  <h4 className="text-sm font-medium text-[var(--ink-2)] mb-2">Detalle de pausas:</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {fichaje.pausas.map((pausa, idx) => {
                                      const tipoPausa = getTipoPausaInfo(pausa.tipo)
                                      const Icono = tipoPausa.icono
                                      return (
                                        <div
                                          key={pausa.id || idx}
                                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${tipoPausa.color}`}
                                        >
                                          <Icono className="h-4 w-4 mr-2" />
                                          <span className="font-medium">{tipoPausa.nombre}:</span>
                                          <span className="ml-2">
                                            {pausa.hora_inicio} - {pausa.hora_fin || 'En curso'}
                                          </span>
                                          {pausa.duracion_minutos > 0 && (
                                            <span className="ml-2 font-medium">
                                              ({formatearDuracion(pausa.duracion_minutos)})
                                            </span>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )
                    })}
                  </tbody>
                </table>
                {fichajes.length === 0 && (
                  <div className="text-center py-12">
                    <FiClock className="mx-auto h-12 w-12 text-[var(--ink-4)]" />
                    <h3 className="mt-3 text-sm font-medium text-[var(--ink)]">Sin fichajes</h3>
                    <p className="mt-1 text-sm text-[var(--ink-3)]">No hay fichajes para mostrar</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tabla de Correcciones (unificada con Control) */}
          {activeTab === 'correcciones' && (
            <div className="space-y-4">
              {/* Sub-pestañas de correcciones — solo Avanzado/Trial */}
              <div className="flex gap-1 border-b border-[var(--line)]">
                {canAccess(plan, 'correcciones_flujo') && (
                <button
                  onClick={() => setSubTabCorrecciones('solicitudes')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    subTabCorrecciones === 'solicitudes'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]'
                  }`}
                >
                  <FiEdit className="h-4 w-4" />
                  Solicitudes
                  {correcciones.filter(c => c.tipo_solicitud !== 'admin_a_empleado').length > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 text-blue-600">
                      {correcciones.filter(c => c.tipo_solicitud !== 'admin_a_empleado').length}
                    </span>
                  )}
                </button>
                )}
                {canAccess(plan, 'correcciones_flujo') && (
                <button
                  onClick={() => setSubTabCorrecciones('ausencias')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    subTabCorrecciones === 'ausencias'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]'
                  }`}
                >
                  <FiCalendar className="h-4 w-4" />
                  Ausencias declaradas
                  {ausenciasPasadas.length > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-purple-100 text-purple-600">
                      {ausenciasPasadas.length}
                    </span>
                  )}
                </button>
                )}
                {canAccess(plan, 'ausencias') && (
                <button
                  onClick={() => setSubTabCorrecciones('permisos')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    subTabCorrecciones === 'permisos'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]'
                  }`}
                >
                  <FiCalendar className="h-4 w-4" />
                  Solicitud Permisos
                  {contadorAusencias > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-green-100 text-green-600">
                      {contadorAusencias}
                    </span>
                  )}
                </button>
                )}
                <button
                  onClick={() => setSubTabCorrecciones('sinFichaje')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    subTabCorrecciones === 'sinFichaje'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]'
                  }`}
                >
                  <FiCheckSquare className="h-4 w-4" />
                  Días sin fichaje
                  {contadorControl > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-orange-100 text-orange-600">
                      {contadorControl}
                    </span>
                  )}
                </button>
              </div>

              {/* Toggle pendientes / histórico (afecta solicitudes, ausencias, permisos) */}
              {(subTabCorrecciones === 'solicitudes' || subTabCorrecciones === 'ausencias' || subTabCorrecciones === 'permisos') && (
                <div className="flex items-center gap-2 -mt-1">
                  <span className="text-xs text-[var(--ink-3)]">Mostrar:</span>
                  <button
                    onClick={() => setEstadoFiltroSolicitudes('pendiente')}
                    className={`px-3 py-1 text-xs rounded-full border transition ${
                      estadoFiltroSolicitudes === 'pendiente'
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--bg-2)]'
                    }`}
                  >
                    Pendientes
                  </button>
                  <button
                    onClick={() => setEstadoFiltroSolicitudes('historico')}
                    className={`px-3 py-1 text-xs rounded-full border transition ${
                      estadoFiltroSolicitudes === 'historico'
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--bg-2)]'
                    }`}
                  >
                    Histórico (aprobadas / rechazadas)
                  </button>
                </div>
              )}

              {/* Contenido: Solicitudes de Corrección */}
              {subTabCorrecciones === 'solicitudes' && (
              <div>
                <div className="space-y-4">
                  {correcciones.map((correccion) => (
                    <div key={correccion.id} className="ui-card p-5">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <FiUser className="mr-2 h-4 w-4 text-[var(--ink-4)]" />
                            <span className="font-medium text-[var(--ink)]">{correccion.empleado_nombre}</span>
                            <span className="ml-2 text-sm text-[var(--ink-3)]">
                              - {formatearFecha(correccion.fecha)}
                            </span>
                          </div>
                          {correccion.pausa_id ? (
                            <div className="mb-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 mb-2">
                                Corrección de Pausa
                              </span>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-[var(--ink-3)]">Pausa original:</span>{' '}
                                  {correccion.hora_inicio_pausa_original || '-'} - {correccion.hora_fin_pausa_original || '-'}
                                </div>
                                <div>
                                  {correccion.solicitar_borrado ? (
                                    <span className="font-medium text-red-600">Solicita borrado de pausa</span>
                                  ) : (
                                    <>
                                      <span className="text-[var(--ink-3)]">Corregido:</span>{' '}
                                      <span className="font-medium text-green-600">
                                        {correccion.hora_inicio_pausa_corregida} - {correccion.hora_fin_pausa_corregida || '-'}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                              <div>
                                <span className="text-[var(--ink-3)]">Original:</span>{' '}
                                {correccion.hora_entrada_original || '-'} - {correccion.hora_salida_original || '-'}
                              </div>
                              <div>
                                <span className="text-[var(--ink-3)]">Corregido:</span>{' '}
                                <span className="font-medium text-green-600">
                                  {correccion.hora_entrada_corregida} - {correccion.hora_salida_corregida || '-'}
                                </span>
                              </div>
                            </div>
                          )}
                          <div className="text-sm text-[var(--ink-2)]">
                            <strong>Motivo:</strong> {correccion.motivo}
                          </div>
                          {correccion.tipo_solicitud === 'admin_a_empleado' && (
                            <div className="text-xs text-[var(--ink-3)] mt-1">
                              Propuesta por: {correccion.solicitante_nombre || 'Admin'}
                            </div>
                          )}
                        </div>
                        {correccion.tipo_solicitud === 'admin_a_empleado' ? (
                          <div className="ml-4 flex items-center">
                            <span className="badge badge-warning text-xs whitespace-nowrap">
                              Pendiente de empleado
                            </span>
                          </div>
                        ) : (
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => abrirModalComentario('correccion', 'aprobar', correccion.id)}
                              className="btn btn-success btn-ripple btn-sm"
                            >
                              <FiCheckCircle className="mr-1" />
                              Aprobar
                            </button>
                            <button
                              onClick={() => abrirModalComentario('correccion', 'rechazar', correccion.id)}
                              className="btn btn-danger btn-ripple btn-sm"
                            >
                              <FiXCircle className="mr-1" />
                              Rechazar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {correcciones.length === 0 && (
                    <div className="ui-card p-5 text-center py-8">
                      <FiAlertCircle className="mx-auto h-10 w-10 text-[var(--ink-4)]" />
                      <p className="mt-2 text-sm text-[var(--ink-3)]">No hay correcciones pendientes</p>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Contenido: Ausencias declaradas (fechas pasadas) */}
              {subTabCorrecciones === 'ausencias' && (
              <div>
                <div className="space-y-3">
                      {ausenciasPasadas.map((ausencia) => (
                        <div key={`aus-${ausencia.id}`} className="ui-card p-5">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <FiUser className="mr-2 h-4 w-4 text-[var(--ink-4)]" />
                                <span className="font-medium text-[var(--ink)]">{ausencia.empleado_nombre}</span>
                                <span className="ml-2 badge badge-primary">{ausencia.tipo}</span>
                              </div>
                              <div className="text-sm mb-2">
                                <FiCalendar className="inline-block mr-1 h-4 w-4 text-[var(--ink-4)]" />
                                <span className="text-[var(--ink-2)]">
                                  {formatearFecha(ausencia.fecha_inicio)} - {formatearFecha(ausencia.fecha_fin)}
                                </span>
                                {ausencia.dias_laborables && (
                                  <span className="ml-2 font-medium">({ausencia.dias_laborables} días)</span>
                                )}
                              </div>
                              {ausencia.motivo && (
                                <div className="text-sm text-[var(--ink-2)]">
                                  <strong>Motivo:</strong> {ausencia.motivo}
                                </div>
                              )}
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => abrirModalComentario('ausencia', 'aprobar', ausencia.id)}
                                className="btn btn-success btn-ripple btn-sm"
                              >
                                <FiCheckCircle className="mr-1" />
                                Aprobar
                              </button>
                              <button
                                onClick={() => abrirModalComentario('ausencia', 'rechazar', ausencia.id)}
                                className="btn btn-danger btn-ripple btn-sm"
                              >
                                <FiXCircle className="mr-1" />
                                Rechazar
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  {ausenciasPasadas.length === 0 && (
                    <div className="ui-card p-5 text-center py-8">
                      <FiCalendar className="mx-auto h-10 w-10 text-[var(--ink-4)]" />
                      <p className="mt-2 text-sm text-[var(--ink-3)]">No hay ausencias declaradas pendientes</p>
                    </div>
                  )}
              </div>
              )}

              {/* Contenido: Días sin Fichaje */}
              {subTabCorrecciones === 'sinFichaje' && (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    {estadoFiltroControl === 'pendiente' && diasSinFichaje.filter(d => d.estado === 'pendiente').length > 1 && (
                      <label className="flex items-center gap-1.5 cursor-pointer text-xs text-[var(--ink-3)] hover:text-[var(--ink-2)]">
                        <input
                          type="checkbox"
                          checked={diasSeleccionados.length === diasSinFichaje.filter(d => d.estado === 'pendiente').length && diasSeleccionados.length > 0}
                          onChange={toggleSeleccionarTodos}
                          className="w-3.5 h-3.5 text-blue-600 rounded border-[var(--line-2)] focus:ring-blue-500"
                        />
                        Seleccionar todos
                      </label>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-[var(--ink-3)] whitespace-nowrap">Estado</label>
                    <select
                      value={estadoFiltroControl}
                      onChange={(e) => setEstadoFiltroControl(e.target.value)}
                      className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] text-sm"
                    >
                      <option value="pendiente">Pendientes</option>
                      <option value="justificado">Justificados</option>
                      <option value="ignorado">Ignorados</option>
                      <option value="todos">Todos</option>
                    </select>
                  </div>
                </div>

                {/* Barra de acciones masivas */}
                {diasSeleccionados.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <span className="text-sm font-medium text-blue-800">
                      {diasSeleccionados.length} día(s) seleccionado(s)
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDiasSeleccionados([])}
                        className="btn btn-secondary btn-sm"
                      >
                        Deseleccionar
                      </button>
                      {canAccess(plan, 'correcciones_flujo') ? (
                        <button
                          onClick={() => {
                            const dias = diasSinFichaje.filter(d => diasSeleccionados.includes(d.id))
                            abrirPropuestaMarcaje(dias)
                          }}
                          className="btn btn-primary btn-ripple btn-sm"
                        >
                          <FiSend className="mr-1" />
                          Proponer marcaje ({diasSeleccionados.length})
                        </button>
                      ) : (
                        <span className="text-xs text-[var(--ink-3)]">
                          Selecciona un día e inserta el fichaje individualmente
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {diasSinFichaje.map((dia) => {
                    const getBadgeClass = (estado) => {
                      switch(estado) {
                        case 'pendiente': return 'badge badge-warning'
                        case 'justificado': return 'badge badge-success'
                        case 'ignorado': return 'badge badge-secondary'
                        default: return 'badge badge-secondary'
                      }
                    }

                    return (
                      <div key={dia.id} className="ui-card p-5">
                        <div className="flex justify-between items-start">
                          {dia.estado === 'pendiente' && (
                            <div className="flex items-start mr-3 pt-1">
                              <input
                                type="checkbox"
                                checked={diasSeleccionados.includes(dia.id)}
                                onChange={() => toggleDiaSeleccionado(dia.id)}
                                className="w-4 h-4 text-blue-600 rounded border-[var(--line-2)] focus:ring-blue-500"
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <FiUser className="mr-2 h-4 w-4 text-[var(--ink-4)]" />
                              <span className="font-medium text-[var(--ink)]">{dia.empleado_nombre}</span>
                              <span className={`ml-2 ${getBadgeClass(dia.estado)}`}>
                                {dia.estado}
                              </span>
                            </div>
                            <div className="text-sm mb-2">
                              <FiCalendar className="inline-block mr-1 h-4 w-4 text-[var(--ink-4)]" />
                              <span className="text-[var(--ink-2)]">
                                {formatearFecha(dia.fecha)}
                              </span>
                            </div>
                            {dia.observaciones && (
                              <div className="text-sm text-[var(--ink-2)]">
                                <strong>Observaciones:</strong> {dia.observaciones}
                              </div>
                            )}
                            {dia.tipo_justificacion && (
                              <div className="text-sm text-[var(--ink-2)]">
                                <strong>Justificado por:</strong> {dia.tipo_justificacion === 'correccion' ? 'Corrección' : 'Ausencia'}
                              </div>
                            )}
                          </div>
                          {dia.estado === 'pendiente' && (
                            <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-2 ml-4">
                              {canAccess(plan, 'correcciones_flujo') ? (
                                <button
                                  onClick={() => abrirPropuestaMarcaje(dia)}
                                  className="btn btn-primary btn-sm"
                                  title="Proponer marcaje al empleado"
                                >
                                  <FiSend className="mr-1" />
                                  Proponer
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setAdminInsertForm({ hora_entrada: '09:00', hora_salida: '17:00', motivo: '' })
                                    setModalAdminInsert(dia)
                                  }}
                                  className="btn btn-sm bg-amber-500 hover:bg-amber-600 text-white"
                                  title="Insertar fichaje directamente"
                                >
                                  <FiPlusCircle className="mr-1" />
                                  Insertar fichaje
                                </button>
                              )}
                              <button
                                onClick={() => abrirModalComentario('control_ignorar', 'ignorar', dia.id)}
                                className="btn btn-secondary btn-sm"
                                title="Ignorar (festivo, dia no laborable, etc.)"
                              >
                                <FiEyeOff className="mr-1" />
                                Ignorar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  {diasSinFichaje.length === 0 && (
                    <div className="ui-card p-5 text-center py-8">
                      <FiCheckSquare className="mx-auto h-10 w-10 text-[var(--ink-4)]" />
                      <p className="mt-2 text-sm text-[var(--ink-3)]">
                        No hay días sin fichaje {estadoFiltroControl !== 'todos' ? `con estado "${estadoFiltroControl}"` : ''}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Contenido: Solicitud de Permisos */}
              {subTabCorrecciones === 'permisos' && (
            <div className="space-y-4">
              {ausencias.map((ausencia) => (
                <div key={ausencia.id} className="ui-card p-5">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <FiUser className="mr-2 h-4 w-4 text-[var(--ink-4)]" />
                        <span className="font-medium text-[var(--ink)]">{ausencia.empleado_nombre}</span>
                        <span className="ml-2 badge badge-primary">
                          {ausencia.tipo}
                        </span>
                      </div>
                      <div className="text-sm mb-2">
                        <FiCalendar className="inline-block mr-1 h-4 w-4 text-[var(--ink-4)]" />
                        <span className="text-[var(--ink-2)]">
                          {formatearFecha(ausencia.fecha_inicio)} - {formatearFecha(ausencia.fecha_fin)}
                        </span>
                        <span className="ml-2 font-medium">({ausencia.dias_laborables} días)</span>
                      </div>
                      {ausencia.motivo && (
                        <div className="text-sm text-[var(--ink-2)]">
                          <strong>Motivo:</strong> {ausencia.motivo}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => abrirModalComentario('ausencia', 'aprobar', ausencia.id)}
                        className="btn btn-success btn-ripple btn-sm"
                      >
                        <FiCheckCircle className="mr-1" />
                        Aprobar
                      </button>
                      <button
                        onClick={() => abrirModalComentario('ausencia', 'rechazar', ausencia.id)}
                        className="btn btn-danger btn-ripple btn-sm"
                      >
                        <FiXCircle className="mr-1" />
                        Rechazar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {ausencias.length === 0 && (
                <div className="ui-card p-5 text-center py-12">
                  <FiCalendar className="mx-auto h-12 w-12 text-[var(--ink-4)]" />
                  <h3 className="mt-3 text-sm font-medium text-[var(--ink)]">Sin solicitudes</h3>
                  <p className="mt-1 text-sm text-[var(--ink-3)]">No hay solicitudes de permisos pendientes</p>
                </div>
              )}
            </div>
          )}
            </div>
          )}
        </>
      )}

      {/* Tab: Horas extras */}
      {activeTab === 'horasExtras' && (
        <SolicitudesHorasExtras embedded />
      )}

      {/* Modal de Comentario */}
      {modalComentario.visible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card p-5 w-full max-w-md">
            <h3 className="text-lg font-semibold text-[var(--ink)] mb-4">
              {modalComentario.titulo}
            </h3>

            <div className="mb-4">
              <label className="label">
                {modalComentario.placeholder}
                {modalComentario.requerido && <span className="text-red-500 ml-1">*</span>}
              </label>
              <textarea
                value={comentarioTexto}
                onChange={(e) => setComentarioTexto(e.target.value)}
                placeholder={modalComentario.placeholder}
                className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full h-24 resize-none"
                autoFocus
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setModalComentario({ ...modalComentario, visible: false })
                  setComentarioTexto('')
                }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={handleEnviarComentario}
                className={`btn btn-ripple ${
                  modalComentario.tipo === 'control_ignorar'
                    ? 'btn-secondary'
                    : modalComentario.accion === 'aprobar'
                      ? 'btn-success'
                      : 'btn-danger'
                }`}
              >
                {modalComentario.tipo === 'control_ignorar' ? 'Ignorar' : modalComentario.accion === 'aprobar' ? 'Aprobar' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Propuesta de Corrección */}
      {fichajeParaPropuesta && (
        <PropuestaCorreccionModal
          fichaje={fichajeParaPropuesta}
          onClose={() => setFichajeParaPropuesta(null)}
          onSuccess={() => {
            setFichajeParaPropuesta(null)
            toast.success('Propuesta enviada al empleado')
          }}
          buildApiUrl={buildApiUrl}
        />
      )}

      {/* Modal Propuesta de Marcaje (dias sin fichaje) */}
      {modalPropuestaMarcaje && (
        <PropuestaMarcajeModal
          diasSeleccionados={modalPropuestaMarcaje}
          onClose={() => setModalPropuestaMarcaje(null)}
          onSuccess={handlePropuestaMarcajeSuccess}
          buildApiUrl={buildApiUrl}
        />
      )}

      {/* Modal: Corregir fichaje directamente (Plan Pro) */}
      {modalAdminEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card p-5 w-full max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <FiEdit2 className="h-5 w-5 text-amber-500" />
              <h3 className="text-lg font-semibold text-[var(--ink)]">Corregir fichaje</h3>
            </div>
            <p className="text-sm text-[var(--ink-3)] mb-4">
              <span className="font-medium text-[var(--ink-2)]">{modalAdminEdit.empleado_nombre}</span>
              {' — '}{formatearFecha(modalAdminEdit.fecha)}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="label">Hora entrada *</label>
                <input
                  type="time"
                  value={adminEditForm.hora_entrada}
                  onChange={e => setAdminEditForm(f => ({ ...f, hora_entrada: e.target.value }))}
                  className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full"
                />
              </div>
              <div>
                <label className="label">Hora salida *</label>
                <input
                  type="time"
                  value={adminEditForm.hora_salida}
                  onChange={e => setAdminEditForm(f => ({ ...f, hora_salida: e.target.value }))}
                  className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="label">Motivo de la corrección *</label>
              <input
                type="text"
                value={adminEditForm.motivo}
                onChange={e => setAdminEditForm(f => ({ ...f, motivo: e.target.value }))}
                placeholder="Ej: Error al registrar la salida"
                className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setModalAdminEdit(null)}
                className="btn btn-secondary"
                disabled={savingAdmin}
              >
                Cancelar
              </button>
              <button
                onClick={handleAdminEditFichaje}
                className="btn btn-ripple bg-amber-500 hover:bg-amber-600 text-white"
                disabled={savingAdmin}
              >
                {savingAdmin ? 'Guardando...' : 'Guardar corrección'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Insertar fichaje faltante (Plan Pro) */}
      {modalAdminInsert && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card p-5 w-full max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <FiPlusCircle className="h-5 w-5 text-amber-500" />
              <h3 className="text-lg font-semibold text-[var(--ink)]">Insertar fichaje</h3>
            </div>
            <p className="text-sm text-[var(--ink-3)] mb-4">
              <span className="font-medium text-[var(--ink-2)]">{modalAdminInsert.empleado_nombre}</span>
              {' — '}{formatearFecha(modalAdminInsert.fecha)}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="label">Hora entrada *</label>
                <input
                  type="time"
                  value={adminInsertForm.hora_entrada}
                  onChange={e => setAdminInsertForm(f => ({ ...f, hora_entrada: e.target.value }))}
                  className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full"
                />
              </div>
              <div>
                <label className="label">Hora salida *</label>
                <input
                  type="time"
                  value={adminInsertForm.hora_salida}
                  onChange={e => setAdminInsertForm(f => ({ ...f, hora_salida: e.target.value }))}
                  className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="label">Motivo *</label>
              <input
                type="text"
                value={adminInsertForm.motivo}
                onChange={e => setAdminInsertForm(f => ({ ...f, motivo: e.target.value }))}
                placeholder="Ej: Empleado olvidó fichar"
                className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] w-full"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setModalAdminInsert(null)}
                className="btn btn-secondary"
                disabled={savingAdmin}
              >
                Cancelar
              </button>
              <button
                onClick={handleAdminInsertFichaje}
                className="btn btn-ripple bg-amber-500 hover:bg-amber-600 text-white"
                disabled={savingAdmin}
              >
                {savingAdmin ? 'Guardando...' : 'Insertar fichaje'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionFichajes
